// A point from what people copy: a Google Maps link (long form), an
// OpenStreetMap link, "lat, lng" in decimal degrees, or the
// degrees-minutes-seconds Google shows ("5°51'07.3"N 55°12'13.8"W").
// Returns { lat, lng } (6 decimals), or { error }: 'short_link' for a
// maps.app.goo.gl / goo.gl link (the browser cannot follow it to the
// coordinates), 'not_found' otherwise.

const round6 = (n) => Math.round(n * 1e6) / 1e6;
const valid = (lat, lng) => Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
const point = (lat, lng) => (valid(lat, lng) ? { lat: round6(lat), lng: round6(lng) } : null);

const NUM = '[-+]?\\d{1,3}(?:\\.\\d+)?';
// "5.85, -55.20", "5.85 -55.20", "5.85;-55.20", "(5.85, -55.20)"
const PAIR = new RegExp(`^\\(?\\s*(${NUM})\\s*[,;\\s]\\s*(${NUM})\\s*\\)?$`);
const PAIR_IN = new RegExp(`(${NUM})\\s*,\\s*(${NUM})`);

// 5°51'07.3"N 55°12'13.8"W (also with ′ ″, º, spaces, or minutes only)
const DMS = /(\d{1,3})\s*[°º]\s*(?:(\d{1,2}(?:\.\d+)?)\s*['′’])?\s*(?:(\d{1,2}(?:\.\d+)?)\s*(?:["″”]|''))?\s*([NSns])[\s,]*(\d{1,3})\s*[°º]\s*(?:(\d{1,2}(?:\.\d+)?)\s*['′’])?\s*(?:(\d{1,2}(?:\.\d+)?)\s*(?:["″”]|''))?\s*([EWOew])/;
const fromDms = (d, m, s, hemi) => {
  const v = Number(d) + (Number(m) || 0) / 60 + (Number(s) || 0) / 3600;
  return /[SsWw]/.test(hemi) ? -v : v;
};

const decode = (s) => {
  try {
    return decodeURIComponent(s.replace(/\+/g, ' '));
  } catch (e) {
    return s;
  }
};

export const parseLocation = (input) => {
  const text = String(input || '').trim();
  if (!text) return { error: 'not_found' };
  if (/^(https?:\/\/)?(maps\.app\.goo\.gl|goo\.gl\/maps)\//i.test(text)) return { error: 'short_link' };

  const plain = PAIR.exec(text);
  if (plain) return point(Number(plain[1]), Number(plain[2])) || { error: 'not_found' };

  const dms = DMS.exec(text);
  if (dms) {
    return point(fromDms(dms[1], dms[2], dms[3], dms[4]), fromDms(dms[5], dms[6], dms[7], dms[8])) || { error: 'not_found' };
  }

  if (/^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/|$|\?)/i.test(text)) {
    const url = decode(text);
    // Google's place data: the place itself (!3d lat !4d lng), more exact
    // than the @ of the view, which is where the screen was centred
    const place = /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/.exec(url);
    if (place) return point(Number(place[1]), Number(place[2])) || { error: 'not_found' };
    // ?q= ?query= ?ll= ?center= ?destination= ?daddr= (Google, Apple), q=loc:
    const param = new RegExp(`[?&](?:q|query|ll|sll|center|destination|daddr)=(?:loc:)?\\s*(${NUM})\\s*,\\s*(${NUM})`, 'i').exec(url);
    if (param) return point(Number(param[1]), Number(param[2])) || { error: 'not_found' };
    // OpenStreetMap: ?mlat=..&mlon=.. or #map=zoom/lat/lng
    const mlat = /[?&]mlat=(-?\d+(?:\.\d+)?)/.exec(url);
    const mlon = /[?&]mlon=(-?\d+(?:\.\d+)?)/.exec(url);
    if (mlat && mlon) return point(Number(mlat[1]), Number(mlon[1])) || { error: 'not_found' };
    const osm = /#map=\d+(?:\.\d+)?\/(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)/.exec(url);
    if (osm) return point(Number(osm[1]), Number(osm[2])) || { error: 'not_found' };
    // Google's view: /@lat,lng,17z
    const at = new RegExp(`/@(${NUM}),(${NUM})`).exec(url);
    if (at) return point(Number(at[1]), Number(at[2])) || { error: 'not_found' };
    const any = PAIR_IN.exec(url);
    if (any) return point(Number(any[1]), Number(any[2])) || { error: 'not_found' };
    return { error: 'not_found' };
  }
  return { error: 'not_found' };
};

/** Roughly Suriname, to warn about a point that is probably wrong (or swapped) */
export const inSuriname = ({ lat, lng }) => lat >= 1.8 && lat <= 6.2 && lng >= -58.2 && lng <= -53.8;
